import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import { PDFDocument, rgb } from 'pdf-lib';
import watermark from '../assets/watermark.png';
import constants from '../constants.js';

const Watermark = () => {
  const [visible, setVisible] = React.useState(false);
  const [value, setValue] = React.useState([]);
  const [submitvalue, setSubmitvalue] = useState(constants.addWatermark);
  const [servicevalue, setServicevalue] = useState([]);
  const [filearray, setfilearray] = useState([]);
  const [totalsize, setTotalsize] = useState(0);
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [watermarkText, setWatermarkText] = useState('');
  const [opacity, setOpacity] = useState('0.3');

  const toggleModal = React.useCallback(
    (service) => () => {
      setVisible(true);
      setValue('');
      setServicevalue(service);
      setSubmitvalue(constants.addWatermark);
      setTotalsize(0);
      setNotify('');
      setError('');
    },
    [],
  );

  const onClickFooterButton = () => {
    setVisible(false);
    setValue('');
    setSubmitvalue(constants.addWatermark);
    setTotalsize(0);
    setNotify('');
    setError('');
    setWatermarkText('');
    setOpacity('0.3');
  };

  async function _handleInputChange(e) {
    setValue(e.detail.value);
    let files = e.detail.value;

    const pdfFiles = files.filter((file) => file.type === constants.pdfFileType);

    if (pdfFiles.length !== files.length) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
    } else {
      setNotify('');
      setError(null);
    }

    let valueAdded = 0;
    for (const file of pdfFiles) {
      valueAdded += file.size;
    }
    setTotalsize(valueAdded / 1000000);
    setfilearray(pdfFiles);
  }

  async function AddingWatermark() {
    setSubmitvalue(<Spinner />);
    let files = filearray;

    if (!watermarkText.trim()) {
      setNotify(constants.enterWatermarkText);
      setError(constants.error);
      setSubmitvalue(constants.addWatermark);
      return;
    }

    for (const file of files) {
      if (file.type === constants.pdfFileType) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const pages = pdfDoc.getPages();

          pages.forEach((page) => {
            const { width, height } = page.getSize();

            // Add diagonal watermark in center
            page.drawText(watermarkText, {
              x: width / 2 - 50,
              y: height / 2,
              size: 50,
              color: rgb(0.7, 0.7, 0.7),
              opacity: parseFloat(opacity),
              rotate: { type: 'degrees', angle: 45 },
            });
          });

          const pdfBytes = await pdfDoc.save();
          const blob = new Blob([pdfBytes], { type: constants.pdfFileType });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `watermarked_${file.name}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (error) {
          console.error('Watermarking failed:', error);
          setNotify(constants.watermarkingFailed);
          setError(constants.error);
        }
      } else {
        setNotify(constants.unsupportedFileType);
        setError(constants.error);
      }
      setWatermarkText('');
      setOpacity('0.3');
    }

    setSubmitvalue(constants.success);
    addUsage('AWESOME PDF TOOLS', '1.0', filearray.length, window.location.href);
    if (error) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }

  return (
    <div>
      <div className="service-card" onClick={toggleModal(constants.addWatermark)}>
        <div className="service-card-icon">
          <img src={watermark} alt="Watermark" loading="lazy" />
        </div>
        <h3 className="service-card-title">Watermark</h3>
        <p className="service-card-desc">Add text watermark to all PDF pages</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
          setWatermarkText('');
          setOpacity('0.3');
        }}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                {constants.cancel}
              </Button>
              <div>
                {totalsize < 2000 && totalsize > 0 && filearray.length > 0 && watermarkText.trim() ? (
                  <Button variant="primary" onClick={AddingWatermark}>
                    {submitvalue}
                  </Button>
                ) : (
                  <Button disabled variant="primary">
                    {totalsize > 2000 ? constants.limitExceeded : submitvalue}
                  </Button>
                )}
              </div>
            </SpaceBetween>
          </Box>
        }
        header={servicevalue}
      >
        {servicevalue === constants.addWatermark ? (
          <div>
            <FormField errorText={notify} label={constants.uploadFile} description={constants.onlyPdfSupported}>
              <FileUpload
                onChange={_handleInputChange}
                value={value}
                i18nStrings={{
                  uploadButtonText: (e) => (e ? 'Choose files' : 'Choose'),
                  dropzoneText: (e) => (e ? 'Drop files to upload' : 'Drop file to upload'),
                  removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                  limitShowFewer: 'Show fewer files',
                  limitShowMore: 'Show more files',
                  errorIconAriaLabel: 'Error',
                }}
                multiple
                showFileLastModified={false}
                showFileSize={false}
                showFileThumbnail={false}
                tokenLimit={9}
                constraintText=""
                accept=".pdf"
              />
            </FormField>

            <FormField label={constants.watermarkText} description={constants.enterTextForWatermark}>
              <Input
                value={watermarkText}
                onChange={({ detail }) => setWatermarkText(detail.value)}
                placeholder={constants.enterWatermarkTextPlaceholder}
              />
            </FormField>

            <FormField label={constants.opacity} description={constants.chooseWatermarkTransparency}>
              <Select
                selectedOption={{ label: `${Math.round(parseFloat(opacity) * 100)}%`, value: opacity }}
                onChange={({ detail }) => setOpacity(detail.selectedOption.value)}
                options={[
                  { label: '10%', value: '0.1' },
                  { label: '20%', value: '0.2' },
                  { label: '30%', value: '0.3' },
                  { label: '50%', value: '0.5' },
                  { label: '70%', value: '0.7' },
                ]}
              />
            </FormField>
          </div>
        ) : (
          <div></div>
        )}
        <br />
        {constants.currentSize} {totalsize.toFixed(2)}
        <span>{constants.mb}</span>
        <br />
        {constants.totalMaxLimit}
      </Modal>
    </div>
  );
};

export default Watermark;
